import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Attempt } from '../types';
import {
  Award,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  BarChart3,
} from 'lucide-react';

export default function Results() {
  const [searchParams] = useSearchParams();
  const attemptId = searchParams.get('attemptId');
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/attempts', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch results');

      const data = await response.json();
      setAttempts(data.attempts || []);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: number) => {
    if (grade === 1) return 'text-green-600';
    if (grade === 2) return 'text-blue-600';
    if (grade === 3) return 'text-yellow-600';
    if (grade === 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const getGradeText = (grade: number) => {
    const grades = ['Sehr gut', 'Gut', 'Befriedigend', 'Genügend', 'Nicht genügend'];
    return grades[grade - 1] || 'Unbekannt';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (attempts.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900">Ergebnisse</h1>
          <p className="mt-2 text-gray-600">Deine Test-Ergebnisse und Fortschritt</p>
        </div>

        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Noch keine Tests absolviert
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Starte deinen ersten Test, um Ergebnisse zu sehen
          </p>
          <div className="mt-6">
            <Link
              to="/topics"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Zu den Themen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">Ergebnisse</h1>
        <p className="mt-2 text-gray-600">
          Du hast {attempts.length} {attempts.length === 1 ? 'Test' : 'Tests'} absolviert
        </p>
      </div>

      {/* Results List */}
      <div className="space-y-4">
        {attempts.map((attempt) => (
          <div
            key={attempt.id}
            className={`bg-white rounded-lg shadow overflow-hidden border-l-4 ${
              attempt.passed ? 'border-green-500' : 'border-red-500'
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    {attempt.passed ? (
                      <CheckCircle className="h-6 w-6 text-green-500 mr-2" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-500 mr-2" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">
                      {attempt.attempt_type === 'lap_simulation'
                        ? 'LAP-Simulation'
                        : 'Thementest'}
                    </h3>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Ergebnis</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {attempt.score_percentage}%
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Note</p>
                      <p
                        className={`text-2xl font-bold ${getGradeColor(
                          attempt.grade || 5
                        )}`}
                      >
                        {attempt.grade ? getGradeText(attempt.grade) : '-'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Datum</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(attempt.completed_at || attempt.started_at).toLocaleDateString(
                          'de-AT',
                          {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(attempt.completed_at || attempt.started_at).toLocaleTimeString(
                          'de-AT',
                          {
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>
                      Status:{' '}
                      <span className="font-medium">
                        {attempt.passed ? 'Bestanden' : 'Nicht bestanden'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/topics"
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Neuer Test
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Zum Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
